"use client";

import React from "react";
import { Euro, FileSearch, Megaphone } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Risque = {
  title: string;
  icon: LucideIcon;
  items: string[];
};
const risques: Risque[] = [{
  title: "Risque financier",
  icon: Euro,
  items: ["Amendes administratives jusqu'à 4\u00a0000\u00a0€ par manquement", "Action réputée inexécutée et remboursement des sommes perçues"]
}, {
  title: "Risque probatoire",
  icon: FileSearch,
  items: ["Montants à reverser établis sur un échantillon, puis extrapolés", "Constatations sous identité d'emprunt pour les actions à distance ou à inscription en ligne", "Droit de reprise porté jusqu'à dix ans dans certains cas"]
}, {
  title: "Risque réputationnel",
  icon: Megaphone,
  items: ["Publicité possible des annulations de déclaration d'activité", "Publicité possible des sanctions pour manœuvres frauduleuses ou manquements graves et répétés"]
}];
export default function WhyChooseEffetConseil() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="pourquoi-faire-appel-à-effet-conseil" className="w-full bg-[#F5EDE8] relative overflow-hidden ![padding:80px_0px_80px_0px]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div ref={ref} className={`max-w-5xl mx-auto text-center transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl font-serif font-bold mb-8 leading-tight !text-[hsl(210,10%,23%)] md:text-5xl">
            Un cadre réglementaire complexe et en constante évolution
          </h2>
          <p className="text-base text-[hsl(210,10%,23%)] font-sans leading-relaxed mb-6">
            La formation professionnelle relève de la sixième partie du Code du
            travail, refondue par la loi «&nbsp;Avenir professionnel&nbsp;» du
            5&nbsp;septembre 2018. Plusieurs autorités en contrôlent
            l'application&nbsp;: les services régionaux de contrôle des DREETS
            (DRIEETS en Île-de-France), les financeurs (Opco, Caisse des dépôts,
            Régions, France Travail, Transitions Pro), France Compétences pour
            les certificateurs et les organismes habilités, la DGCCRF pour les
            pratiques commerciales. La certification Qualiopi® ne se substitue à
            aucun de ces contrôles.
          </p>
          <p className="text-base text-[hsl(210,10%,23%)] font-sans leading-relaxed mb-8">
            Depuis le 27&nbsp;juin 2026, la loi n°&nbsp;2026-534 du
            25&nbsp;juin 2026 renforce trois risques pour les organismes.
            Certaines mesures attendent encore leur décret d'application.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4 text-left">
            {risques.map((risque, index) => <div key={index} data-index={index} className="bg-white rounded-xl border border-[#d6cfc4]/60 shadow-sm p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-[hsl(343,80%,35%)]/10 text-[hsl(343,80%,35%)] shrink-0">
                    <risque.icon className="w-6 h-6" aria-hidden="true" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-[hsl(343,80%,35%)]">
                    {risque.title}
                  </h3>
                </div>
                <ul className="space-y-3">
                  {risque.items.map((item, i) => <li key={i} className="flex items-start gap-2 text-sm leading-relaxed text-[hsl(210,10%,23%)]">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#62751D] shrink-0" aria-hidden="true" />
                      <span>{item}</span>
                    </li>)}
                </ul>
              </div>)}
          </div>
          <p className="text-xs text-[hsl(210,10%,23%)]/60 font-sans leading-relaxed mb-10 text-left">
            Loi n°&nbsp;2026-534 du 25&nbsp;juin 2026&nbsp;; Code du travail,
            art.&nbsp;L.&nbsp;6356-1 à L.&nbsp;6356-7, L.&nbsp;6362-3,
            L.&nbsp;6361-3, L.&nbsp;6362-8-1, L.&nbsp;6362-8-2,
            L.&nbsp;6362-12-1, L.&nbsp;6362-12-2 et L.&nbsp;6362-13.
          </p>

          <p className="text-base text-[hsl(210,10%,23%)] font-sans leading-relaxed mb-6">
            Effet Conseil accompagne les organismes dans la mise en conformité
            de leurs pratiques. L'approche repose sur la vulgarisation des
            textes, la traduction des obligations en pratiques concrètes et la
            mise en place d'une veille réglementaire adaptée à votre activité.
          </p>
          <p className="text-base text-[hsl(210,10%,23%)] font-sans leading-relaxed font-bold">
            L'engagement&nbsp;: transformer les obligations légales en pratiques
            claires, documentées et pérennes, prêtes pour un contrôle, pour que
            vous exerciez votre activité en toute sérénité.
          </p>
        </div>
      </div>
    </section>;
}